import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Card, Grid, Stack, Typography } from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PaymentsIcon from "@mui/icons-material/Payments";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { collection, getCountFromServer, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatDigits, formatJalali, kabulIsoDay, kabulToday } from "../lib/format";
import { PageHeader, SectionTitle, StatCard } from "../components/ui";
import { useT } from "../i18n";
import type { StringKey } from "../i18n/fa";

/** How far along a new owner is: halls registered / sent for review / live. */
interface HallProgress {
  total: number;
  pending: number;
  published: number;
}

interface InsightCard {
  icon: string;
  title: string;
  body: string;
  tone: string;
}

interface Kpis {
  confirmed: number;
  pendingRequests: number;
  pendingDeposits: number;
  upcoming7d: number;
}

/**
 * The insights API tags each card with an emoji. Emoji render differently per
 * OS and clash with the panel's icon set, so map them to Material icons here;
 * the API contract is unchanged and an unknown tag falls back to a neutral icon.
 */
const INSIGHT_ICON: Record<string, typeof InsightsOutlinedIcon> = {
  "📊": BarChartOutlinedIcon,
  "💰": PaymentsOutlinedIcon,
  "⏳": HourglassTopOutlinedIcon,
  "🧾": ReceiptLongOutlinedIcon,
};

const TONE_ACCENT: Record<string, string> = {
  success: "#1B7A43",
  warning: "#9A6700",
  info: "#25638F",
};

export function DashboardPage() {
  const { orgId } = useSession();
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [insights, setInsights] = useState<InsightCard[]>([]);
  const [halls, setHalls] = useState<HallProgress | null>(null);
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => {
    if (!orgId) return;
    api<{ cards: InsightCard[] }>(`/orgs/${orgId}/ai/insights`)
      .then((r) => setInsights(r.cards))
      .catch(() => setInsights([]));
  }, [orgId]);

  // Drives the getting-started card: a brand-new owner has nothing live yet.
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(
      query(collection(db, "halls"), where("orgId", "==", orgId)),
      (snap) => {
        const statuses = snap.docs.map((d) => d.data().status as string);
        setHalls({
          total: statuses.length,
          pending: statuses.filter((s) => s === "pending_review").length,
          published: statuses.filter((s) => s === "published").length,
        });
      },
      () => setHalls(null),
    );
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const bookings = collection(db, "bookings");
    // Kabul's calendar day, not the UTC one (see kabulIsoDay).
    const today = kabulToday();
    const week = kabulIsoDay(new Date(Date.now() + 7 * 86_400_000));

    // allSettled, not all: one failing count (e.g. an index not yet built in
    // production) must not leave all four cards spinning forever.
    Promise.allSettled([
      getCountFromServer(
        query(bookings, where("orgId", "==", orgId), where("status", "==", "confirmed")),
      ),
      getCountFromServer(
        query(bookings, where("orgId", "==", orgId), where("status", "==", "pending_request")),
      ),
      getCountFromServer(
        query(bookings, where("orgId", "==", orgId), where("status", "==", "pending_deposit")),
      ),
      getCountFromServer(
        query(
          bookings,
          where("orgId", "==", orgId),
          where("status", "==", "confirmed"),
          where("date", ">=", today),
          where("date", "<=", week),
        ),
      ),
    ]).then((results) => {
      const [confirmed, requests, deposits, upcoming] = results.map((r) =>
        r.status === "fulfilled" ? r.value.data().count : NaN,
      );
      setKpis({ confirmed, pendingRequests: requests, pendingDeposits: deposits, upcoming7d: upcoming });
    });
  }, [orgId]);

  const today = kabulToday();
  // A failed count is NaN: show "—" rather than a misleading 0.
  const kpi = (n: number | undefined) => (n === undefined ? undefined : Number.isNaN(n) ? "—" : n);
  const showStart = halls !== null && halls.published === 0;
  const steps: { key: StringKey; done: boolean }[] = [
    { key: "start_step_hall", done: (halls?.total ?? 0) > 0 },
    { key: "start_step_menu", done: false },
    { key: "start_step_publish", done: (halls?.pending ?? 0) > 0 },
  ];

  return (
    <>
      <PageHeader
        title={t("dashboard_title")}
        subtitle={t("layout_today", { date: formatJalali(today) })}
      />

      {showStart && (
        <Card sx={{ p: 2.5, mb: 2, borderInlineStart: "3px solid #0E6B5C" }}>
          <Typography variant="subtitle1" fontWeight={800} mb={1.5}>
            {t("start_title")}
          </Typography>
          <Stack spacing={1.25} mb={2}>
            {steps.map((step, i) => (
              <Stack key={step.key} direction="row" spacing={1.25} alignItems="center">
                {step.done ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                )}
                <Typography
                  variant="body2"
                  sx={{ color: step.done ? "text.secondary" : "text.primary" }}
                >
                  {formatDigits(i + 1)}. {t(step.key)}
                </Typography>
              </Stack>
            ))}
          </Stack>
          {(halls?.pending ?? 0) > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("start_pending")}
            </Alert>
          )}
          <Button variant="contained" onClick={() => navigate("/halls")}>
            {t("start_go_halls")}
          </Button>
        </Card>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<EventAvailableIcon />}
            tone="success"
            label={t("dashboard_kpi_confirmed")}
            value={kpi(kpis?.confirmed)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PendingActionsIcon />}
            tone="warning"
            label={t("dashboard_kpi_pending_requests")}
            value={kpi(kpis?.pendingRequests)}
            hint={kpis?.pendingRequests ? t("dashboard_kpi_pending_requests_hint") : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PaymentsIcon />}
            tone="info"
            label={t("dashboard_kpi_pending_deposits")}
            value={kpi(kpis?.pendingDeposits)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CalendarMonthIcon />}
            tone="primary"
            label={t("dashboard_kpi_upcoming")}
            value={kpi(kpis?.upcoming7d)}
          />
        </Grid>
      </Grid>

      {insights.length > 0 && (
        <>
          <SectionTitle>{t("dashboard_insights_title")}</SectionTitle>
          <Grid container spacing={2}>
            {insights.map((card) => {
              const accent = TONE_ACCENT[card.tone] ?? "#0E6B5C";
              return (
                <Grid item xs={12} md={6} key={card.title}>
                  <Card
                    sx={{
                      p: 2,
                      height: "100%",
                      borderInlineStart: `3px solid ${accent}`,
                    }}
                  >
                    <Stack direction="row" spacing={1.5}>
                      {(() => {
                        const Icon = INSIGHT_ICON[card.icon] ?? InsightsOutlinedIcon;
                        return <Icon aria-hidden sx={{ color: accent, fontSize: 24, mt: 0.25 }} />;
                      })()}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} dir="auto">
                          {card.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.25} dir="auto">
                          {card.body}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </>
  );
}
